import { Box, Button, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import aiMusicGenerator from 'services/locales/en/aiMusicGenerator';
import { ControlComponentProps } from '../../types';
import Counter from './Counter';
import useInstrumentsSelector, { Value } from './useInstrumentsSelector';
import { instruments, Shuffle } from './icons';

type InstrumentsSelectorProps = ControlComponentProps<Value>;

const InstrumentsSelector = ({ value, onChange }: InstrumentsSelectorProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const {
    availableInstruments,
    changeInstrumentCount,
    randomise,
    totalUnits,
    maxUnits,
    getMaxInstrumentUnits,
  } = useInstrumentsSelector({ value, onChange });

  return (
    <Box display="flex" flexDirection="column" gap={theme.spacing(1)}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          borderColor: theme.palette.grey[700],
          borderWidth: 1,
          borderTopStyle: 'solid',
          borderBottomStyle: 'solid',
          paddingY: theme.spacing(0.25),
        }}
      >
        <Typography
          sx={{
            color: theme.palette.grey[400],
            fontSize: 14,
            letterSpacing: 0.15,
          }}
        >
          {totalUnits}/{maxUnits}
        </Typography>
        <Button
          sx={{
            fontSize: 14,
            letterSpacing: 0.15,
            fontWeight: 'normal',
            color: theme.palette.grey[400],
            textTransform: 'none',
            '&:hover': {
              background: 'none',
              color: theme.palette.grey[50],
            },
          }}
          endIcon={<Shuffle />}
          onClick={randomise}
        >
          Randomise
        </Button>
      </Box>

      <Box display="flex" flexDirection="column" gap={theme.spacing(1)}>
        {availableInstruments.map((instrument) => (
          <Box
            key={instrument}
            sx={{
              display: 'grid',
              gridTemplateColumns: 'max-content auto  max-content',
              alignItems: 'center',
              gap: theme.spacing(1),
              color: value[instrument]
                ? theme.palette.grey[50]
                : theme.palette.grey[600],
            }}
          >
            <Box component={instruments[instrument]} />
            <Typography fontSize={theme.spacing(1.75)} letterSpacing={0.15}>
              {t(aiMusicGenerator.instruments[instrument])}
            </Typography>
            <Counter
              min={0}
              max={getMaxInstrumentUnits(instrument)}
              value={value[instrument] || 0}
              onChange={(newValue) =>
                changeInstrumentCount(instrument, newValue)
              }
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default InstrumentsSelector;
